import { Link } from 'react-router-dom'
import { Mail, MapPin, MessageSquare, Building2, ArrowRight, HelpCircle } from 'lucide-react'
import SEO from '@/components/common/SEO'

/**
 * 연락처(Contact) — 문의 채널·운영 주체 정보.
 * 애드센스 신뢰 페이지 요건. petpro.me 레이아웃 완성도 참고(브랜드 블루 유지).
 * 영구지침: docs/plan/2026-06-08-adsense-content-strategy.md §5 Phase A
 */
export default function ContactPage() {
  return (
    <>
      <SEO
        title="연락처 · 문의"
        description="케어밸런스 서비스 이용 문의, 시설 정보 정정 요청, 제휴 문의 등 연락 방법을 안내합니다. 이메일 contact@carebalance.co.kr."
        url="/contact"
      />

      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1e3a8a] via-[#2563eb] to-[#4338ca]">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.07]"
          style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '22px 22px' }}
        />
        <div className="relative mx-auto max-w-5xl px-6 py-20 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-1.5 text-xs font-semibold text-white backdrop-blur-sm">
            <MessageSquare size={13} /> 문의하기
          </span>
          <h1 className="mt-5 text-3xl font-bold leading-tight text-white sm:text-4xl">무엇을 도와드릴까요?</h1>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-white/80">
            서비스 이용 문의, 시설 정보 정정 요청, 제휴·광고 문의 등 무엇이든 편하게 연락 주세요.
            영업일 기준 1~2일 내에 답변드립니다.
          </p>
        </div>
      </section>

      {/* ── 문의 채널 ────────────────────────────────────── */}
      <section className="relative z-10 mx-auto mt-6 max-w-5xl px-6 sm:-mt-10">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-cb-gray-100 bg-white p-7 shadow-md">
            <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cb-primary/10 text-cb-primary">
              <Mail size={20} />
            </div>
            <h2 className="text-lg font-bold text-cb-gray-900">이메일 문의</h2>
            <p className="mt-1.5 text-body-sm leading-relaxed text-cb-gray-600">
              가장 빠르게 연락할 수 있는 방법입니다. 문의 내용을 보내주세요.
            </p>
            <a href="mailto:contact@carebalance.co.kr" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-cb-primary px-5 py-3 text-sm font-bold text-white hover:bg-cb-primary/90">
              <Mail size={16} /> contact@carebalance.co.kr
            </a>
          </div>
          <div className="rounded-2xl border border-cb-gray-100 bg-white p-7 shadow-md">
            <div className="mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-cb-primary/10 text-cb-primary">
              <MessageSquare size={20} />
            </div>
            <h2 className="text-lg font-bold text-cb-gray-900">1:1 문의</h2>
            <p className="mt-1.5 text-body-sm leading-relaxed text-cb-gray-600">
              회원이시라면 마이페이지에서 접수와 답변 내역을 한곳에서 관리할 수 있습니다.
            </p>
            <Link to="/mypage/inquiry/new" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-cb-primary px-5 py-3 text-sm font-bold text-cb-primary hover:bg-cb-primary/5">
              1:1 문의 작성하기 <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── 자주 묻는 문의 ───────────────────────────────── */}
      <section className="mx-auto max-w-5xl px-6 py-16">
        <div className="mb-8 flex items-center justify-center gap-2">
          <HelpCircle size={20} className="text-cb-primary" />
          <h2 className="text-2xl font-bold text-cb-gray-900">자주 묻는 문의</h2>
        </div>
        <div className="mx-auto max-w-3xl space-y-3">
          <Faq
            q="시설 정보가 실제와 달라요."
            a={<>공공데이터 갱신 지연일 수 있습니다. 시설명과 함께 정정 요청을 보내주시면 확인 후 반영합니다. 시설운영자라면 <ExtA href="https://biz.carebalance.co.kr">시설관리자 페이지</ExtA>에서 직접 수정할 수 있습니다.</>}
          />
          <Faq
            q="우리 시설을 등록·관리하고 싶어요."
            a={<><ExtA href="https://biz.carebalance.co.kr">시설관리자 가입</ExtA> 후 무료로 시설 정보를 관리할 수 있습니다.</>}
          />
          <Faq q="제휴·광고를 문의하고 싶어요." a={<>위 이메일(contact@carebalance.co.kr)로 제휴 제안서를 보내주세요.</>} />
        </div>
      </section>

      {/* ── 운영 주체 ────────────────────────────────────── */}
      <section className="bg-cb-gray-50 py-16">
        <div className="mx-auto max-w-2xl px-6">
          <div className="mb-5 flex items-center justify-center gap-2">
            <Building2 size={18} className="text-cb-gray-500" />
            <h2 className="text-xl font-bold text-cb-gray-900">운영 주체</h2>
          </div>
          <div className="rounded-2xl border border-cb-gray-200 bg-white p-7 text-body-sm leading-relaxed text-cb-gray-700 shadow-sm">
            <p className="font-semibold text-cb-gray-900">파인드플레이스 (CareBalance)</p>
            <p className="mt-1">대표 우상철 · 사업자등록번호 587-18-02704</p>
            <p className="mt-1 flex items-start gap-1.5">
              <MapPin size={14} className="mt-0.5 shrink-0 text-cb-gray-400" />
              경기도 의정부시 서부로 545 경민대학교 창업관 7층 754-1호 파인드플레이스
            </p>
            <p className="mt-4 border-t border-cb-gray-100 pt-4 text-caption text-cb-gray-500">
              회사에 대한 자세한 내용은 <Link to="/about" className="font-semibold text-cb-primary hover:underline">회사소개</Link>를 참고해 주세요.
            </p>
          </div>
        </div>
      </section>
    </>
  )
}

function Faq({ q, a }: { q: string; a: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-cb-gray-200 bg-white p-5 shadow-sm">
      <p className="font-bold text-cb-gray-900">Q. {q}</p>
      <p className="mt-2 text-body-sm leading-relaxed text-cb-gray-600">{a}</p>
    </div>
  )
}

function ExtA({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-cb-primary hover:underline">
      {children}
    </a>
  )
}
